import { interpolate, spring } from 'remotion';

// Constants
import { CLAMP_BOTH } from '@/constants';
import {
  ANIMATION_START_FRAME,
  EXIT_DURATION,
  EXIT_STAGGER_DELAY,
  FLIP_STAGGER_DELAY,
  FLIP_START_DELAY,
  HOLD_DURATION,
  PHASE_FLIP_TO_HOLD_BUFFER,
  SLIDE_DISTANCE,
  STAGGER_DELAY,
} from './constants';

// Utils
import { getSceneExitProgress } from '../../utils';

// Types
import { TeamMember } from '../../types';

// Spring configs
const SLIDE_SPRING_CONFIG = {
  damping: 14,
  stiffness: 80,
  mass: 1.2,
};

const FLIP_SPRING_CONFIG = {
  damping: 16,
  stiffness: 100,
  mass: 1,
};

interface PhaseAnimation {
  translateY: number;
  opacity: number;
  flipProgress: number;
}

interface PhaseRendererProps {
  members: TeamMember[];
  phaseStartFrame: number;
  fps: number;
  currentFrame: number;
  hasExit: boolean;
  sceneExitStartFrame?: number; // Scene exit start frame
  sceneExitDuration?: number; // Scene exit duration in frames
}

export const usePhaseAnimations = ({
  members,
  phaseStartFrame,
  fps,
  currentFrame,
  hasExit,
  sceneExitStartFrame,
  sceneExitDuration,
}: PhaseRendererProps): PhaseAnimation[] => {
  const cardCount = members.length;
  const localFrame = currentFrame - phaseStartFrame;

  // Last card finishes sliding in at this frame
  const lastSlideDelay =
    ANIMATION_START_FRAME + (cardCount - 1) * STAGGER_DELAY;

  // Flip animation starts after last slide completes
  const flipStartFrame = lastSlideDelay + FLIP_START_DELAY;

  // Last card finishes flipping at this frame
  const lastFlipDelay = flipStartFrame + (cardCount - 1) * FLIP_STAGGER_DELAY;

  // Per-phase exit starts after last flip and hold period
  const exitStartFrame =
    lastFlipDelay + PHASE_FLIP_TO_HOLD_BUFFER + HOLD_DURATION;

  // Scene exit progress from 0 to 1
  const sceneExitProgress = getSceneExitProgress(
    currentFrame,
    sceneExitStartFrame,
    sceneExitDuration,
  );

  return members.map((_, index) => {
    // Even indices slide from top (-1), odd from bottom (1)
    const direction = index % 2 === 0 ? -1 : 1;

    // Slide-in animation with stagger delay
    const slideDelay = ANIMATION_START_FRAME + index * STAGGER_DELAY;
    const slideProgress = spring({
      frame: localFrame - slideDelay,
      fps,
      config: SLIDE_SPRING_CONFIG,
    });

    // Card slides from off-screen to final position
    const slideTranslateY = interpolate(
      slideProgress,
      [0, 1],
      [direction * SLIDE_DISTANCE, 0],
    );

    // Opacity fades in during first 40% of slide
    const slideOpacity = interpolate(slideProgress, [0, 0.4], [0, 1], {
      extrapolateRight: 'clamp',
    });

    // Flip animation starts after slide completes
    const flipDelay = flipStartFrame + index * FLIP_STAGGER_DELAY;
    const flipProgress = spring({
      frame: localFrame - flipDelay,
      fps,
      config: FLIP_SPRING_CONFIG,
    });

    // Exit animation: scene exit takes precedence over per-phase exit
    let exitTranslateY = 0;

    if (sceneExitProgress > 0) {
      // Scene exit: cards revert in opposite direction
      exitTranslateY = interpolate(
        sceneExitProgress,
        [0, 1],
        [0, -direction * SLIDE_DISTANCE],
      );
    } else if (hasExit) {
      // Per-phase exit: cards slide out for next group
      const exitDelay = exitStartFrame + index * EXIT_STAGGER_DELAY;
      const exitProgress = interpolate(
        localFrame - exitDelay,
        [0, EXIT_DURATION],
        [0, 1],
        CLAMP_BOTH,
      );

      exitTranslateY = interpolate(
        exitProgress,
        [0, 1],
        [0, -direction * SLIDE_DISTANCE],
      );
    }

    return {
      translateY: slideTranslateY + exitTranslateY,
      opacity: slideOpacity,
      flipProgress,
    };
  });
};
